import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useFinance } from "@/context/FinanceContext";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Wallet, Filter } from "lucide-react";
import { currencyFormatterFull } from "@/lib/formatters";
import { DateInput } from "@/components/ui/date-input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

export function AccountBalances() {
  const { transactions, accounts } = useFinance();
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split("T")[0]);
  const [visibleAccountIds, setVisibleAccountIds] = useState<Set<string> | null>(null);

  const balanceData = useMemo(() => {
    // Calcular saldo para cada conta até a data selecionada
    const accountBalances = accounts.map(account => {
      // Saldo inicial
      let balance = account.initialBalance || 0;
      
      // Somar/subtrair transações liquidadas desta conta até a data selecionada
      // Usa payment_date (data real do pagamento) quando disponível, senão due_date
      const accountTransactions = transactions.filter(t => {
        if (t.accountId !== account.id || !t.settled) return false;
        
        // Data efetiva: payment_date tem prioridade (quando o dinheiro realmente movimentou)
        const effectiveDate = t.paymentDate || t.dueDate;
        
        // Se liquidada mas sem nenhuma data, inclui (dinheiro já movimentou)
        if (!effectiveDate) return true;
        
        // Se não há data selecionada, não filtra
        if (!selectedDate) return true;
        
        // Extrair apenas ano/mês/dia da data da transação (ignora timezone)
        // Formato esperado: "YYYY-MM-DD"
        const [year, month, day] = effectiveDate.split('-').map(Number);
        const transactionDateOnly = new Date(year, month - 1, day);
        transactionDateOnly.setHours(0, 0, 0, 0);
        
        // Data selecionada normalizada para final do dia (local)
        const [selYear, selMonth, selDay] = selectedDate.split('-').map(Number);
        const selectedDateEnd = new Date(selYear, selMonth - 1, selDay);
        selectedDateEnd.setHours(23, 59, 59, 999);
        
        // Comparar as datas normalizadas
        return transactionDateOnly <= selectedDateEnd;
      });
      
      accountTransactions.forEach(t => {
        if (t.type === "receita") {
          balance += t.value;
        } else {
          balance -= t.value;
        }
      });

      return {
        ...account,
        currentBalance: balance
      };
    });

    // Filtrar por contas visíveis
    const filteredBalances = visibleAccountIds
      ? accountBalances.filter(a => visibleAccountIds.has(a.id))
      : accountBalances;

    // Calcular total
    const totalBalance = filteredBalances.reduce((sum, acc) => sum + acc.currentBalance, 0);

    // Ordenar por saldo (maior primeiro)
    const sortedBalances = [...filteredBalances].sort((a, b) => b.currentBalance - a.currentBalance);

    return {
      accountBalances: sortedBalances,
      totalBalance,
      allAccountBalances: accountBalances
    };
  }, [transactions, accounts, selectedDate, visibleAccountIds]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg font-semibold">
            <Wallet className="h-5 w-5" />
            Saldos
          </CardTitle>
          
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="icon" className="h-8 w-8">
                  <Filter className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 bg-popover z-50" align="end">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Contas visíveis</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs"
                      onClick={() => setVisibleAccountIds(null)}
                    >
                      Todas
                    </Button>
                  </div>
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {accounts.map(account => {
                      const isVisible = !visibleAccountIds || visibleAccountIds.has(account.id);
                      return (
                        <label key={account.id} className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={isVisible}
                            onCheckedChange={(checked) => {
                              setVisibleAccountIds(prev => {
                                const current = prev || new Set(accounts.map(a => a.id));
                                const next = new Set(current);
                                if (checked) {
                                  next.add(account.id);
                                } else {
                                  next.delete(account.id);
                                }
                                // Se todas selecionadas, voltar para null
                                if (next.size === accounts.length) return null;
                                return next;
                              });
                            }}
                          />
                          <span className="text-sm truncate">{account.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            <div className="w-full sm:w-[140px]">
              <DateInput
                value={selectedDate}
                onChange={(value) => value && setSelectedDate(value)}
                className="h-8 text-sm"
              />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 pb-4 px-4">
        {/* Lista de contas */}
        <div className="max-h-[280px] overflow-y-auto -mx-1 px-1 scrollbar-thin">
          {balanceData.accountBalances.map((account, index) => {
            const maxBalance = Math.max(...balanceData.accountBalances.map(a => Math.abs(a.currentBalance)), 1);
            const barWidth = Math.max((Math.abs(account.currentBalance) / maxBalance) * 100, 2);
            
            return (
              <div
                key={account.id}
                className={`group relative flex items-center justify-between py-2.5 px-3 rounded-lg transition-colors hover:bg-muted/50 ${
                  index !== balanceData.accountBalances.length - 1 ? 'border-b border-border/40' : ''
                }`}
              >
                {/* Background bar indicator */}
                <div
                  className={`absolute left-0 top-0 bottom-0 rounded-lg opacity-[0.06] transition-opacity group-hover:opacity-[0.12] ${
                    account.currentBalance >= 0 ? 'bg-emerald-500' : 'bg-destructive'
                  }`}
                  style={{ width: `${barWidth}%` }}
                />
                
                <div className="relative flex items-center gap-2.5 flex-1 min-w-0 mr-3">
                  <div
                    className={`w-2 h-2 rounded-full shrink-0 ${
                      account.currentBalance > 0
                        ? 'bg-emerald-500'
                        : account.currentBalance < 0
                        ? 'bg-destructive'
                        : 'bg-muted-foreground/40'
                    }`}
                  />
                  <span className="text-sm truncate text-foreground/80">
                    {account.name}
                  </span>
                </div>
                
                <span className={`relative text-sm font-medium tabular-nums whitespace-nowrap ${
                  account.currentBalance > 0
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : account.currentBalance < 0
                    ? 'text-destructive'
                    : 'text-muted-foreground'
                }`}>
                  {currencyFormatterFull.format(account.currentBalance)}
                </span>
              </div>
            );
          })}
          
          {balanceData.accountBalances.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nenhuma conta encontrada
            </p>
          )}
        </div>

        {/* Total */}
        <div className="mt-3 pt-3 border-t-2 border-border/60 mx-2">
          <div className="flex justify-between items-center">
            <span className="text-sm font-semibold text-foreground">Total</span>
            <span className={`text-base font-bold tabular-nums ${
              balanceData.totalBalance >= 0
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-destructive'
            }`}>
              {currencyFormatterFull.format(balanceData.totalBalance)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
